import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TradingSignalsPanel = () => {
  const [activeTab, setActiveTab] = useState('signals');

  const tradingSignals = [
    {
      id: 1,
      symbol: 'BTC',
      name: 'Bitcoin',
      signal: 'BUY',
      strength: 'Strong',
      price: 45250.00,
      target: 48500.00,
      stopLoss: 43800.00,
      timeframe: '4H',
      confidence: 89,
      timestamp: new Date(Date.now() - 300000),
      status: 'active'
    },
    {
      id: 2,
      symbol: 'ETH',
      name: 'Ethereum',
      signal: 'SELL',
      strength: 'Medium',
      price: 2845.50,
      target: 2650.00,
      stopLoss: 2950.00,
      timeframe: '1D',
      confidence: 76,
      timestamp: new Date(Date.now() - 900000),
      status: 'active'
    },
    {
      id: 3,
      symbol: 'ADA',
      name: 'Cardano',
      signal: 'BUY',
      strength: 'Weak',
      price: 0.485,
      target: 0.520,
      stopLoss: 0.465,
      timeframe: '2H',
      confidence: 68,
      timestamp: new Date(Date.now() - 1800000),
      status: 'pending'
    }
  ];

  const alerts = [
    {
      id: 1,
      type: 'price',
      message: 'BTC reached target price of $48,000',
      timestamp: new Date(Date.now() - 600000),
      severity: 'success',
      read: false
    },
    {
      id: 2,
      type: 'signal',
      message: 'New SELL signal generated for ETH',
      timestamp: new Date(Date.now() - 1200000),
      severity: 'warning',
      read: false
    },
    {
      id: 3,
      type: 'portfolio',
      message: 'Portfolio value increased by 5.2%',
      timestamp: new Date(Date.now() - 2400000),
      severity: 'info',
      read: true
    }
  ];

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'BUY': return 'text-success bg-success/10 border-success/20';
      case 'SELL': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'Strong': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'Weak': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success': return 'text-success bg-success/10';
      case 'warning': return 'text-warning bg-warning/10';
      case 'info': return 'text-primary bg-primary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ago`;
    return `${minutes}m ago`;
  };

  const SignalsTab = () => (
    <div className="space-y-4">
      {tradingSignals?.map((signal) => (
        <div key={signal?.id} className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-standard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold text-secondary">{signal?.symbol}</span>
              </div>
              <div>
                <h3 className="font-medium text-card-foreground">{signal?.name}</h3>
                <p className="text-sm text-muted-foreground">{signal?.timeframe} • {formatTime(signal?.timestamp)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getSignalColor(signal?.signal)}`}>
                {signal?.signal}
              </span>
              <span className={`text-xs font-medium ${getStrengthColor(signal?.strength)}`}>
                {signal?.strength}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
            <div>
              <p className="text-xs text-muted-foreground">Entry Price</p>
              <p className="font-medium text-card-foreground">${signal?.price?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Target</p>
              <p className="font-medium text-success">${signal?.target?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Stop Loss</p>
              <p className="font-medium text-error">${signal?.stopLoss?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Confidence</p>
              <p className="font-medium text-card-foreground">{signal?.confidence}%</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${signal?.status === 'active' ? 'bg-success' : 'bg-warning'}`}></div>
              <span className="text-xs text-muted-foreground capitalize">{signal?.status}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="xs">
                <Icon name="Copy" size={12} />
                Copy
              </Button>
              <Button variant="ghost" size="xs">
                <Icon name="MoreHorizontal" size={12} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const AlertsTab = () => (
    <div className="space-y-3">
      {alerts?.map((alert) => (
        <div key={alert?.id} className={`p-4 rounded-lg border transition-standard ${alert?.read ? 'border-border bg-muted/10' : 'border-primary/20 bg-primary/5'}`}>
          <div className="flex items-start space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSeverityColor(alert?.severity)}`}>
              <Icon 
                name={alert?.type === 'price' ? 'DollarSign' : alert?.type === 'signal' ? 'Zap' : 'TrendingUp'} 
                size={16} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-card-foreground">{alert?.message}</p>
              <p className="text-xs text-muted-foreground mt-1">{formatTime(alert?.timestamp)}</p>
            </div>
            {!alert?.read && (
              <div className="w-2 h-2 bg-primary rounded-full"></div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Trading Signals</h2>
              <p className="text-sm text-muted-foreground">AI-powered trading recommendations</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/trading-signals'}>
            View All
          </Button>
        </div>

        <div className="flex space-x-1 bg-muted/30 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('signals')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-standard ${
              activeTab === 'signals' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Signals ({tradingSignals?.length})
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-standard ${
              activeTab === 'alerts' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Alerts ({alerts?.filter(a => !a?.read)?.length})
          </button>
        </div>
      </div>
      <div className="p-6">
        {activeTab === 'signals' ? <SignalsTab /> : <AlertsTab />}
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Activity" size={16} className="text-primary" />
            <span className="text-muted-foreground">Signal accuracy: 87.3% (last 30 days)</span>
          </div>
          <Button variant="ghost" size="sm">
            <Icon name="Settings" size={16} />
            Configure
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TradingSignalsPanel;